import { useMemo, useState } from 'react'
import type { Equipment } from '@numeo/types-core'
import type { BoxTruckVerification } from '../services/boxTruckLoadsApi'
import { Button, Modal, Select } from 'components/ds2'
import { applyTruckDraftUpdate, hasAnyTruckInput, truckDraftFromVerification, type TruckDraft } from '../utils/truckFromVerification'
import { ClaimDriverTruckFields } from './ClaimDriverTruckFields'

export interface ClaimDriverSubmit {
    leadSource?: string
    /** Absent when the operator left the vehicle fields empty — adding the driver alone stays possible. */
    truck?: TruckDraft
}

interface ClaimDriverModalProps {
    driver: BoxTruckVerification
    leadSources: string[]
    /** Controlled by the page, which also owns the "+ Add new source" flow: a source added there has to come
     *  back SELECTED, and it cannot reach state that lives in here. */
    leadSource: string
    onLeadSourceChange: (value: string) => void
    submitting: boolean
    onClose: () => void
    onSubmit: (values: ClaimDriverSubmit) => void
    /** The "+ Add new source" affordance, rendered by the page that owns the source list. */
    sourceExtra?: React.ReactNode
}

/**
 * "Add my driver" — the driver, and now the truck they told us about.
 *
 * Claiming used to create the driver and nothing else. The vehicle the driver had described when they
 * registered — VIN, truck number, equipment, make, model, cargo box — stayed prose on their record, and the
 * vehicle documents an operator gathered afterwards had no asset to attach to, so they went nowhere. This is
 * the one moment where all of it is on screen and the operator has already decided to take the driver on.
 */
export const ClaimDriverModal = ({
    driver,
    leadSources,
    leadSource,
    onLeadSourceChange,
    submitting,
    onClose,
    onSubmit,
    sourceExtra,
}: ClaimDriverModalProps) => {
    const [truck, setTruck] = useState<TruckDraft>(() => truckDraftFromVerification(driver.boxTruckInfo.info))
    const name = driver.boxTruckInfo.info?.name || driver.name || 'this driver'

    const updateTruck = (field: string, value: string | number | null | boolean | Equipment | string[]) =>
        setTruck((current) => applyTruckDraftUpdate(current, field, value))

    const wantsTruck = useMemo(() => hasAnyTruckInput(truck), [truck])

    // NOT gated on VIN here. The Verifications queue is cross-tenant and withholds an unclaimed driver's VIN
    // while exposing their equipment type, so the prefilled draft is ALWAYS half a vehicle — demanding both
    // up front made the carry-over unreachable for exactly the row it was built for, asking the operator to
    // retype a VIN the page will not show them. The completed truck is checked after the claim, where the
    // driver's own record has filled the blanks in.
    const submit = () => onSubmit({ leadSource: leadSource || undefined, truck: wantsTruck ? truck : undefined })

    return (
        <Modal
            open
            onClose={() => !submitting && onClose()}
            size="cover"
            header={<h2 className="text-base font-semibold text-foreground">Add driver</h2>}
            footer={
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" size="sm" loading={submitting} onClick={submit}>
                        {wantsTruck ? 'Add driver and truck' : 'Add to Drivers'}
                    </Button>
                </div>
            }
        >
            <p className="text-sm text-foreground">
                Add <span className="font-semibold">{name}</span> as your driver?
            </p>
            <p className="mt-2 text-xs text-foreground-muted">
                They&apos;ll appear in Profiles → Drivers right away and you can control which features they see in the mobile app. The driver keeps their own
                account — no action needed on their side.
            </p>

            <div className="mt-4">
                <p className="mb-1.5 text-xs font-medium text-foreground-muted">Lead source (optional)</p>
                <Select
                    placeholder="Select a source"
                    value={leadSource}
                    onChange={onLeadSourceChange}
                    options={[{ label: 'No source', value: '' }, ...leadSources.map((s) => ({ label: s, value: s }))]}
                />
                {sourceExtra}
            </div>

            <ClaimDriverTruckFields truck={truck} onUpdate={updateTruck} />
        </Modal>
    )
}
